const $ = jQuery;

const cameraControllers = new WeakMap();

function setCameraPresentation(modal, enabled) {
    const button = modal.find('.modal-videocall-actions-camera');
    button
        .toggleClass('is-active', enabled)
        .attr('aria-pressed', enabled ? 'true' : 'false')
        .attr('aria-label', enabled ? 'Turn off camera' : 'Turn on camera');
    modal.toggleClass('has-local-camera', enabled);
}

function createCameraController(modal) {
    const video = modal.find('.modal-videocall-local-camera').get(0);
    const characterPip = modal.find('.modal-videocall-character-pip');
    const characterPipVideo = modal.find('.modal-videocall-character-pip-video').get(0);
    const error = modal.find('.modal-videocall-camera-error');
    let stream = null;
    let requestId = 0;
    let backgroundVideo = null;
    let pipSourceUrl = '';

    function syncCharacterVideo() {
        if (!backgroundVideo || !characterPipVideo) return;

        const sourceUrl = backgroundVideo.currentSrc
            || (backgroundVideo.querySelector('source') || {}).src
            || '';

        if (!sourceUrl) {
            characterPip.removeClass('has-video');
            return;
        }

        const syncPlaybackPosition = function () {
            if (!backgroundVideo || !Number.isFinite(backgroundVideo.currentTime)) return;
            if (Number.isFinite(characterPipVideo.duration) && characterPipVideo.duration > 0) {
                characterPipVideo.currentTime = Math.min(backgroundVideo.currentTime, characterPipVideo.duration - 0.05);
            }
            characterPipVideo.playbackRate = backgroundVideo.playbackRate || 1;
            characterPipVideo.play().catch(function () {});
        };

        if (pipSourceUrl !== sourceUrl) {
            pipSourceUrl = sourceUrl;
            characterPipVideo.src = sourceUrl;
            characterPipVideo.addEventListener('loadedmetadata', syncPlaybackPosition, { once: true });
            characterPipVideo.load();
        } else if (Math.abs(characterPipVideo.currentTime - backgroundVideo.currentTime) > 0.5) {
            syncPlaybackPosition();
        }

        characterPip.addClass('has-video');
    }

    function startCharacterVideo() {
        backgroundVideo = modal.find('.video-wrapper video[data-video-playlist]').get(0) || null;
        if (!backgroundVideo || !characterPipVideo) return;

        ['loadedmetadata', 'canplay', 'play', 'timeupdate', 'ratechange'].forEach(function (eventName) {
            backgroundVideo.addEventListener(eventName, syncCharacterVideo);
        });
        syncCharacterVideo();
    }

    function stopCharacterVideo() {
        if (backgroundVideo) {
            ['loadedmetadata', 'canplay', 'play', 'timeupdate', 'ratechange'].forEach(function (eventName) {
                backgroundVideo.removeEventListener(eventName, syncCharacterVideo);
            });
        }
        backgroundVideo = null;
        pipSourceUrl = '';
        characterPip.removeClass('has-video');
        if (characterPipVideo) {
            characterPipVideo.pause();
            characterPipVideo.removeAttribute('src');
            characterPipVideo.load();
        }
    }

    function clearError() {
        error.text('').removeClass('is-visible');
    }

    function showError(message) {
        error.text(message).addClass('is-visible');
    }

    function stop() {
        requestId += 1;
        stopCharacterVideo();
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            stream = null;
        }
        if (video) {
            video.pause();
            video.srcObject = null;
        }
        modal.find('.modal-videocall-actions-camera').prop('disabled', false);
        setCameraPresentation(modal, false);
        clearError();
    }

    async function start() {
        if (stream || !video) return;

        if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
            showError('Camera is not available in this browser.');
            return;
        }

        const currentRequestId = ++requestId;
        const button = modal.find('.modal-videocall-actions-camera');
        button.prop('disabled', true);
        clearError();

        try {
            const nextStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user' },
                audio: false,
            });

            if (currentRequestId !== requestId) {
                nextStream.getTracks().forEach((track) => track.stop());
                return;
            }

            stream = nextStream;
            stream.getVideoTracks().forEach((track) => {
                track.addEventListener('ended', stop, { once: true });
            });
            video.srcObject = stream;
            await video.play();
            startCharacterVideo();
            setCameraPresentation(modal, true);
        } catch (cameraError) {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
                stream = null;
            }
            video.srcObject = null;
            setCameraPresentation(modal, false);
            showError(
                cameraError && cameraError.name === 'NotAllowedError'
                    ? 'Camera access was blocked. Allow camera access and try again.'
                    : 'Could not start the camera. Please try again.'
            );
        } finally {
            if (currentRequestId === requestId) {
                button.prop('disabled', false);
            }
        }
    }

    function toggle() {
        if (stream) {
            stop();
            return;
        }
        start();
    }

    return {
        start,
        stop,
        toggle,
        isActive: function () { return Boolean(stream); },
    };
}

export function setupVideocallCamera(modal, options = {}) {
    const element = modal.get(0);
    if (!element) return { stop: function() {} };

    let controller = cameraControllers.get(element);
    if (!controller) {
        controller = createCameraController(modal);
        cameraControllers.set(element, controller);
    }

    modal.find('.modal-videocall-actions-camera')
        .off('click.videocallCamera')
        .on('click.videocallCamera', function () {
            const callIsActive = modal.attr('data-conversation-active') === 'true';
            if (!controller.isActive() && !callIsActive && typeof options.startCall === 'function') {
                const callStarted = options.startCall();
                if (callStarted === false) return;
            }
            controller.toggle();
        });

    return controller;
}

export function stopVideocallCamera(modal) {
    const element = modal && modal.get ? modal.get(0) : null;
    const controller = element ? cameraControllers.get(element) : null;
    if (controller) controller.stop();
}
